import React from 'react';
import { Step, Stepper, StepButton, Box, Theme } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useCampaignForm } from 'hooks';
import { BillingMethod, CampaignFields, CampaignStep, CampaignStepState } from 'types';
import { orderedSteps, stepIndices } from 'shared';

const getWarnedStepStyles = (theme: Theme) => ({
  '.MuiStepLabel-iconContainer, .MuiStepLabel-labelContainer, .Mui-active': {
    color: `${theme.palette.warning.dark} !important`,
  },
  '.MuiStepLabel-labelContainer': {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  '.MuiStepLabel-iconContainer': {
    width: theme.spacing(4),
    height: theme.spacing(4),
    position: 'relative',
    '&::after': {
      position: 'absolute',
      left: 14,
      top: -4,
      display: 'block',
      height: '2px',
      width: '2px',
      padding: '2px',
      borderRadius: '100%',
      content: "''",
      backgroundColor: 'warning.dark',
    },
  },
  '.MuiSvgIcon-root': {
    width: theme.spacing(4.8),
    height: theme.spacing(4.8),
  },
});

const getDefaultStepStyles = (theme: Theme) => ({
  '.MuiStepLabel-iconContainer, .Mui-active': {
    color: `${theme.palette.primary.main} !important`,
  },
  '.MuiStepLabel-labelContainer': {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  '.MuiStepLabel-iconContainer': {
    width: theme.spacing(4),
    height: theme.spacing(4),
    position: 'relative',
  },
  '.MuiSvgIcon-root': {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  '.MuiStepIcon-text': {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 900,
  },
});

const getDisabledStepStyles = (theme: Theme) => ({
  '.MuiStepLabel-iconContainer, .Mui-active': {
    color: `${theme.palette.grey[400]} !important`,
  },
  '.MuiStepLabel-labelContainer': {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: `${theme.palette.grey[400]} !important`,
  },
  '.MuiStepLabel-iconContainer': {
    width: theme.spacing(4),
    height: theme.spacing(4),
    position: 'relative',
  },
  '.MuiSvgIcon-root': {
    width: theme.spacing(4),
    height: theme.spacing(4),
    color: `${theme.palette.grey[400]} !important`,
  },
  '.MuiStepIcon-text': {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 900,
    color: `${theme.palette.common.white} !important`,
  },
});

export const CampaignStepper = () => {
  const { activeStep, stepsData, redirectToStep, watch, isCreateCampaignFlow } = useCampaignForm();

  const isClickBillingMethod = () => watch(CampaignFields.billingMethod) === BillingMethod.CLICK;

  const getIsStepDisabled = (step: CampaignStep) => {
    if (activeStep === step) return true;
    if (step === CampaignStep.GA && isClickBillingMethod()) return true;
    return isCreateCampaignFlow && step !== CampaignStep.Basic;
  };

  const getIsStepComplete = (step: CampaignStep, state: CampaignStepState) =>
    state === CampaignStepState.Complete && !(step === CampaignStep.GA && isClickBillingMethod());

  return (
    <Stepper activeStep={stepIndices[activeStep]} connector={null} alternativeLabel>
      {orderedSteps.map((step) => {
        const { label, state } = stepsData[step];

        return (
          <Step key={step} completed={getIsStepComplete(step, state)} disabled={getIsStepDisabled(step)}>
            <StepButton
              role="tab"
              color="inherit"
              value={step}
              onClick={() => redirectToStep(step)}
              icon={state === CampaignStepState.Warning && <ErrorIcon fontSize="small" />}
              sx={(theme) => {
                // Check if this step is disabled due to click billing
                const isStepDisabled = step === CampaignStep.GA && isClickBillingMethod();

                if (isStepDisabled) {
                  return getDisabledStepStyles(theme);
                } else if (state === CampaignStepState.Warning) {
                  return getWarnedStepStyles(theme);
                } else {
                  return getDefaultStepStyles(theme);
                }
              }}
            >
              {label}
            </StepButton>
            {activeStep === step && (
              <Box
                sx={{
                  borderTop: 3,
                  borderTopLeftRadius: 100,
                  borderTopRightRadius: 100,
                  borderColor: (() => {
                    const isStepDisabled = step === CampaignStep.GA && isClickBillingMethod();
                    if (isStepDisabled) {
                      return 'grey.400'; // Use disabled color for GA4 when click billing
                    } else if (state === CampaignStepState.Warning) {
                      return 'warning.dark';
                    } else {
                      return 'rose.500';
                    }
                  })(),
                }}
              />
            )}
          </Step>
        );
      })}
    </Stepper>
  );
};
